import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2, Sparkles } from 'lucide-react';
import aiPublicApi from '../../services/aiPublicApi';

const SUGGESTED_QUESTIONS = [
  'What is TAURIS?',
  'Does TAURIS support manufacturing?',
  'How does inventory management work?',
  'Can TAURIS handle distributors?',
  'What reports are available?',
  'Is TAURIS suitable for my business?',
];

export default function AIPublicAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [configured, setConfigured] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    aiPublicApi.status().then((d) => setConfigured(d.configured)).catch(() => setConfigured(false));
  }, []);

  const addMessage = (role, content) => {
    setMessages((prev) => [...prev, { role, content, timestamp: Date.now() }]);
  };

  const handleSend = async (text) => {
    const msg = (text || input).trim();
    if (!msg || isLoading) return;

    setInput('');
    addMessage('user', msg);
    setIsLoading(true);
    setMessages((prev) => [...prev, { role: 'assistant', content: '', timestamp: Date.now(), streaming: true }]);

    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      let fullReply = '';

      for await (const token of aiPublicApi.chatStream(msg, history)) {
        fullReply += token;
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], content: fullReply, streaming: false };
          return updated;
        });
      }
    } catch (err) {
      try {
        const history = messages.map((m) => ({ role: m.role, content: m.content }));
        const result = await aiPublicApi.chat(msg, history);
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], content: result.reply, streaming: false };
          return updated;
        });
      } catch (fallbackErr) {
        const errorMessage = fallbackErr.response?.data?.error || fallbackErr.message;
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: `Sorry, I encountered an error: ${errorMessage}. Please try again.`,
            streaming: false,
            error: true,
          };
          return updated;
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#09b8a9] text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center justify-center group"
          aria-label="Chat with TAURIS AI Assistant"
        >
          <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white animate-pulse" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-6rem)] flex flex-col rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-[#09b8a9]/5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#09b8a9]/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#09b8a9]" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#383b50]">TAURIS AI Assistant</h3>
                <p className="text-xs text-gray-500">
                  {configured ? 'Ask me about TAURIS' : 'Not configured'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={() => { setMessages([]); setInput(''); }}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  title="Clear chat"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-[#09b8a9]/10 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-[#09b8a9]" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#383b50]">Welcome to TAURIS</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Your AI product expert — ask anything about the platform
                  </p>
                </div>
                {!configured && (
                  <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs">
                    AI assistant is not configured yet.
                  </div>
                )}
                <div className="w-full space-y-2">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => handleSend(q)}
                      className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-600 hover:text-[#383b50] hover:bg-gray-50 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-lg bg-[#09b8a9]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="w-4 h-4 text-[#09b8a9]" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#09b8a9] text-white rounded-br-md'
                      : 'bg-gray-100 text-[#383b50] rounded-bl-md'
                  } ${msg.error ? 'bg-red-50 text-red-700 border border-red-200' : ''}`}
                >
                  {msg.streaming && !msg.content ? (
                    <div className="flex items-center gap-1.5 py-1">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span className="text-xs text-gray-400">Thinking...</span>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded-lg bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-gray-100">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about TAURIS..."
                rows={1}
                className="flex-1 resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#09b8a9]/30 focus:border-[#09b8a9] max-h-24"
                style={{ minHeight: '42px' }}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading}
                className="w-10 h-10 rounded-xl bg-[#09b8a9] text-white flex items-center justify-center hover:bg-[#07a295] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">
              AI responses may be inaccurate. Verify critical information.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
